/**
 * Les plages de tonalité d'un morceau, lues sur ses sections.
 *
 * L'analyseur compte les degrés depuis la tonalité du morceau, sauf dans les
 * sections qu'il lit sur la relative — le couplet en ré mineur d'un morceau en
 * fa majeur — où chaque accord porte `localKey`. Rien sur la page ne signale
 * ce passage : les deux tonalités relatives partagent l'armure. C'est donc à
 * l'écran de le dire, et ce module réduit les sections à ce qu'il faut pour
 * ça : une suite de plages contiguës, chacune sous une tonique, avec le lien
 * de chaque plage à la tonalité du morceau.
 *
 * Deux sections voisines sous la même tonique font une seule plage : ce qui
 * se signale est le changement, pas la section.
 */

import type { AnalysisKey, AnalysisSection, RelativeKeyRef } from "@/lib/music/analyzer-types";
import { relativeTonality, type Tonality, type TonalityMode } from "@/lib/music/tonality";

/**
 * Ce que la plage est à la tonalité du morceau.
 *
 * - `home` : la tonalité du morceau elle-même ;
 * - `relative` : sa relative, même armure, autre tonique ;
 * - `parallel` : l'homonyme, même tonique, autre mode ;
 * - `other` : une autre tonalité.
 */
export type KeyRelation = "home" | "relative" | "parallel" | "other";

export type KeySpan = {
  readonly fromTick: number;
  readonly toTick: number;
  readonly tonic: string;
  readonly mode: TonalityMode;
  readonly relation: KeyRelation;
};

/** Ce que `keySpansOf` lit d'une analyse. */
export type KeySpanSource = {
  readonly key: Pick<AnalysisKey, "tonic" | "mode">;
  readonly sections: readonly Pick<AnalysisSection, "startTick" | "endTick" | "localKey">[];
};

/**
 * Les plages de tonalité, dans l'ordre du morceau.
 *
 * Vide quand aucune section ne quitte la tonalité du morceau : il n'y a alors
 * rien à signaler, et un écran qui recevrait une plage unique « home » aurait
 * à décider de ne pas la dessiner.
 */
export function keySpansOf({ key, sections }: KeySpanSource): KeySpan[] {
  const home: Tonality = { tonic: key.tonic, mode: key.mode };
  const ordered = [...sections].sort((a, b) => a.startTick - b.startTick);
  const spans: KeySpan[] = [];

  for (const section of ordered) {
    if (section.endTick <= section.startTick) {
      continue;
    }

    const local = section.localKey ?? null;
    const tonality: Tonality = local ? { tonic: local.tonic, mode: local.mode } : home;
    const last = spans[spans.length - 1];

    if (last && last.tonic === tonality.tonic && last.mode === tonality.mode) {
      spans[spans.length - 1] = { ...last, toTick: Math.max(last.toTick, section.endTick) };

      continue;
    }

    spans.push({
      fromTick: section.startTick,
      toTick: section.endTick,
      tonic: tonality.tonic,
      mode: tonality.mode,
      relation: relationOf(tonality, home),
    });
  }

  if (spans.every((span) => span.relation === "home")) {
    return [];
  }

  return spans;
}

/** Le lien d'une tonalité à celle du morceau. */
export function relationOf(tonality: Tonality, home: Tonality): KeyRelation {
  if (tonality.tonic === home.tonic && tonality.mode === home.mode) {
    return "home";
  }

  const relative = relativeTonality(home);

  if (tonality.tonic === relative.tonic && tonality.mode === relative.mode) {
    return "relative";
  }

  if (tonality.tonic === home.tonic) {
    return "parallel";
  }

  return "other";
}

/** La tonalité locale d'une plage, sous la forme que l'analyseur publie. */
export function keyRefOf(span: KeySpan): RelativeKeyRef {
  return { tonic: span.tonic, mode: span.mode, label: `${span.tonic} ${span.mode}` };
}
